/**
 * 「TA 的星球生活」首屏（UI-WORLD-LIVING-117-3，参考稿 05-life-A；ADDENDUM-12：「首屏是 TA 在自己家里的场景大图」）。
 * - 底图：小窝同一套屋内底图（HOME-LAYERS 的 room-plate，../../home/homeStage 的 PLATES.inside）。只用白天那张：
 *   公开居民数据里没有天色、作息，小窝切夜景靠后端作息给的天色，不按看的人本机时钟猜。
 * - 取景（SCENE_VIEW）：比小窝拉近一些，地毯和 TA 在中间，TA 的脚落在下沿渐隐开始之前（评审 R1：原来 TA 只占宽 27%，屋子比 TA 抢眼）。
 * - TA：公开的官方形象（透明底整身图）由程序叠在地毯前那一点（inside.rest.1），大小照小窝的远近比例（bodyHeight）。
 *   画 TA 的是单独一个组件 SceneFigure：6796 的动作播放器好了以后整个换掉它，位置和大小照旧由 sceneLayout 算好交进去（I 10-16）。
 *   访客经访客那条路取图，取不取由主体给的访客对象定（主页公开、回包是这一位才给，同原来的头像闸）；登录后直接用公开地址。
 *   没有官方形象（照片、插画）就不叠：方的照片放进屋里像一张贴纸。叠不上时主体在名字旁放头像（原来那套退回链）。
 * - 官方形象有两种（元数据分不出来，评审 R2 量到 30 位里 17 位是不透明的棚拍照）：透明底的整身图站在地毯前；不透明的当成一张照片，
 *   装进纸边相框立在地毯上（不是一块贴在地上的灰色长方形）。图读出来以后在页面里画到一块小画布上看四个角是不是透明，看之前先不露出来；
 *   画不了（没有画布）就当透明底。以后透明底的多姿态素材（BASIC-PACK-117）到了，这里就都是站着的 TA。
 * - 只在 TA 此刻在家（presence＝at_home）时叠（useSceneFigure 只在家时给图）；出门了屋里空着，并写一句「这会儿不在家」（评审 R1：空屋子要说为什么空）。
 *   在家却叠不上（没有官方形象、打不开）时主体不放首屏（不留一间读屏说「在家」却空着的屋子，评审 R2）。
 * - 睡着了（asleep＝true）：形象照旧是站姿，所以调暗，不说成睡姿；「睡着了」那句在 TA 的一天里说。
 * - 底图打不开：整块不出（不留一只悬空的 TA），主体改在名字旁放头像（评审 R1）。
 * - 整块对读屏是一张图：说「{名字}在家」或「{名字}这会儿不在家」，底图和 TA 本身不进读屏。
 */
import { useState, type CSSProperties } from "react";
import type { PublicResident } from "@/shared/contracts";
import { anchorPoint, bodyHeight, FRAME, PLATES, plateScale } from "@/features/home/homeStage";
import { photoWidthFor, sizedPhotoSrc, usePhotoSizes } from "@/shared/ui/photoSizes";
import { publicIdentityBinding, usePublicIdentityMedia, type GuestIdentitySubject } from "../publicIdentityMedia";

/** 首屏露出的那一块舞台（舞台按宽 780 算，见 homeStage 的 FRAME）：宽 600、高 520，地毯和 TA 在中间 */
export const SCENE_VIEW = { left: 110, right: 710, top: 420, bottom: 940 } as const;
/** TA 落脚的那一点：小窝屋里的 1 号窝（地毯前沿） */
export const SCENE_ANCHOR = "inside.rest.1";
/** 官方形象图（2:3）里，主体占整张高的多少、脚底在第几成（P114 出图规格：上下留白，主体底边约在 7/8 处） */
const BODY_SHARE = 0.75;
const FOOT_AT = 0.875;

type Box = { left: number; top: number; width?: number; height: number };

/** 底图、TA 在首屏里的位置（百分比，纯算术，用例直接核） */
export function sceneLayout() {
  const { left, right, top, bottom } = SCENE_VIEW;
  const w = right - left;
  const h = bottom - top;
  const plate = PLATES.inside;
  const k = plateScale("inside");
  const plateTop = FRAME.top - plate.cropTop * k;
  const foot = anchorPoint("inside", SCENE_ANCHOR);
  const figureHeight = bodyHeight("inside", foot.y) / BODY_SHARE;
  return {
    aspect: `${w} / ${h}`,
    plate: { left: (-left / w) * 100, width: (FRAME.width / w) * 100, top: ((plateTop - top) / h) * 100, height: ((plate.height * k) / h) * 100 } as Box,
    figure: { left: ((foot.x - left) / w) * 100, top: ((foot.y - figureHeight * FOOT_AT - top) / h) * 100, height: (figureHeight / h) * 100 } as Box,
  };
}

/** 首屏 TA 的图：src 是叠进去的图；pending＝访客那条路还在取（这时名字旁也先不放头像，免得取到以后整段往上跳，评审 R1 高） */
export type SceneFigureState = { src: string | null; pending: boolean };

/**
 * 叠进屋里的那张图：只在 TA 此刻在家、有官方形象时才有。官方形象由主体按头像那套判断给（official：居民那一栏标了官方、主页和居民那一栏的地址对得上；
 *   对不上按契约不会发生，照原来的居民主页不当官方形象读，也就不叠）。访客只在主体给了访客对象（guestSubject）时经访客那条路取；
 * 取不到、打不开 src 就是 null（主体改放头像）。登录后直接用公开地址（带缩略档）。
 */
export function useSceneFigure(resident: PublicResident | null, official: boolean, guest: boolean, guestSubject: GuestIdentitySubject | null): SceneFigureState {
  const wanted = Boolean(resident && resident.presence === "at_home" && official && resident.avatar_url);
  const sized = usePhotoSizes();
  const binding = guest && wanted && resident ? publicIdentityBinding(guestSubject, resident.avatar_url, true) : null;
  const media = usePublicIdentityMedia(binding, sized ? photoWidthFor(384) : null);
  if (!wanted || !resident?.avatar_url) return { src: null, pending: false };
  if (guest) return !binding || media.failed ? { src: null, pending: false } : { src: media.src, pending: !media.src };
  return { src: sized ? sizedPhotoSrc(resident.avatar_url, 384) : resident.avatar_url, pending: false };
}

/** 相框（不透明的照片）：脚那一点在相框底边正中，高是站着的 TA 的八成（宽高比 3:4 由样式给） */
function frameBox(figure: Box): Box {
  const height = figure.height * 0.8;
  return { left: figure.left, top: figure.top + figure.height * FOOT_AT - height, height };
}

/**
 * 透明底还是不透明：把图画到一块 8×12 的小画布上，看四个角的透明度——至少三个角几乎全透明就是透明底的整身图。
 * 画不了（没有画布、读不出像素）就当透明底（照旧站在屋里）。一次读完整块（不连读四次）。
 * 官方图要和页面同源（访客那条路是 blob、登录后是相对地址）；以后若换成没开 CORS 的跨域 CDN，读不出像素，会全当透明底（评审 R3 记）。
 */
export function looksCutout(image: HTMLImageElement): boolean {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 8;
    canvas.height = 12;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return true;
    context.drawImage(image, 0, 0, 8, 12);
    const pixels = context.getImageData(0, 0, 8, 12).data;
    const alpha = (x: number, y: number) => pixels[(y * 8 + x) * 4 + 3];
    return [alpha(0, 0), alpha(7, 0), alpha(0, 11), alpha(7, 11)].filter((value) => value < 128).length >= 3;
  } catch {
    return true;
  }
}

const pct = (box: Box): CSSProperties => ({
  left: `${box.left}%`, top: `${box.top}%`, height: `${box.height}%`, ...(box.width === undefined ? {} : { width: `${box.width}%` }),
});

/**
 * 屋里的 TA：透明底的整身图脚落在 box 算好的那一点；不透明的照片装进相框立在同一点（读出来、看过四个角之前先不露出来）。
 * 睡着了调暗。打不开交给主体（主体撤掉首屏、改放头像）。6796 的动作播放器好了以后整个换掉这个组件。
 */
export function SceneFigure({ src, asleep, box, onError }: { src: string; asleep: boolean; box: Box; onError: () => void }) {
  const [seen, setSeen] = useState<{ src: string; cutout: boolean } | null>(null);
  const kind = seen?.src === src ? (seen.cutout ? "cutout" : "photo") : "checking";
  const dim = asleep ? " is-asleep" : "";
  if (kind === "photo") {
    return <span className={`ps-life-scene__frame${dim}`} style={pct(frameBox(box))}><img src={src} alt="" draggable={false} onError={onError} /></span>;
  }
  return <img className={`ps-life-scene__figure${dim}${kind === "checking" ? " is-checking" : ""}`} src={src} alt="" draggable={false} style={pct(box)}
    onLoad={(event) => setSeen({ src, cutout: looksCutout(event.currentTarget) })} onError={onError} />;
}

export function LifeScene({ resident, name, figure, onFigureError, onPlateError }: {
  resident: PublicResident;
  name: string;
  figure: string | null;
  onFigureError: (src: string) => void;
  onPlateError: () => void;
}) {
  const layout = sceneLayout();
  const home = resident.presence === "at_home";
  return <div className="ps-life-scene" style={{ "--scene-aspect": layout.aspect } as CSSProperties} role="img" aria-label={home ? `${name}在家` : `${name}这会儿不在家`}>
    <img className="ps-life-scene__plate" src={PLATES.inside.src} alt="" draggable={false} style={pct(layout.plate)} onError={onPlateError} />
    {figure ? <SceneFigure src={figure} asleep={resident.asleep === true} box={layout.figure} onError={() => onFigureError(figure)} /> : null}
    {home ? null : <p className="ps-life-scene__away" aria-hidden="true">{name}这会儿不在家</p>}
  </div>;
}
